/*
  LSC - Article card width guard and row balancer (shared)
  Created 14 Oct 2026 (word breaks). Row balancing added 14 Oct 2026
  (balanced rows).

  Loaded by snippets/lsc-article-row.liquid for every article card row:
    sections/lsc-ingredient-articles.liquid            product page, ingredients
    sections/lsc-concern-articles.liquid               product page, skin concerns
    sections/lsc-concern-ingredient-articles.liquid    concern article, "Ingredients that help"

  JOB 1: CARD WIDTH (word breaks)
  Card titles, text and buttons wrap only between words (rule in
  assets/lsc-article-cards.css). A card narrower than its longest word would
  then let that word run past the card edge. This script finds, for each row,
  the narrowest card width that fits the longest single word on ANY card
  (title, text or button, plus the card's own side padding), and stores it on
  the row as --lsc-card-min. The stylesheet makes every card in the row at
  least that wide, so all cards stay the same width and no word is split.
  Each item is briefly given width: min-content (its longest unbreakable
  piece), measured, and put back within one synchronous pass, so nothing is
  painted in between.

  JOB 2: BALANCED ROWS (phone "Tiles, centered" only)
  Rows that wrap normally fill greedily: 7 cards with room for 3 per row give
  3 + 3 + 1. For rows with .lsc-arow--m-center on a phone (under 750px), the
  cards are instead spread as evenly as possible over the same number of
  rows: 3 + 2 + 2. Larger rows come first. This is done by inserting an empty
  <li class="lsc-arow__break" aria-hidden="true"> after the last card of each
  row; the stylesheet turns it into a full-width, zero-height flex item that
  starts a new line (and hides it everywhere else). Breaks are removed and
  rebuilt whenever the row width, card width or screen size changes.

  WHEN IT RUNS
  - once when the script loads (it is deferred, so the page is parsed);
  - again when web fonts finish loading (fonts change word widths);
  - when a balanced row's track changes width (ResizeObserver), and when the
    screen crosses the 750px phone breakpoint;
  - in the theme editor, again for a section whenever it is re-rendered
    (shopify:section:load), since a setting change replaces its markup.

  Safe to load more than once per page (each row prints the tag): the browser
  fetches the file once, and the guard below skips a second set-up.
*/
(function () {
  'use strict';

  /* LSC - Run the set-up only once per page, however many rows load the file. */
  if (window.lscArticleCardWidth) return;

  /* LSC - The items whose longest word must fit inside the card. */
  var ITEM_SELECTOR = '.lsc-arow__title, .lsc-arow__text, .lsc-arow__btn';

  /* LSC - Phone width, matching the stylesheet's mobile breakpoint. */
  var PHONE = window.matchMedia('(max-width: 749px)');

  /* ---------------------------------------------------------------------- */
  /* JOB 1: CARD WIDTH                                                       */
  /* ---------------------------------------------------------------------- */

  /*
    LSC - Width an element needs so that no word is split: its min-content
    width. The current inline width is restored immediately.
  */
  function minContentWidth(el) {
    var previous = el.style.width;
    el.style.width = 'min-content';
    var width = el.getBoundingClientRect().width;
    el.style.width = previous;
    return width;
  }

  /*
    LSC - Measure one row (.lsc-arow root) and set --lsc-card-min on it.
    The card link's side padding (Card padding, used with a card background)
    is added, because the items sit inside it.
  */
  function measureRow(root) {
    var links = root.querySelectorAll('.lsc-arow__link');
    var need = 0;

    for (var i = 0; i < links.length; i++) {
      var link = links[i];
      var style = getComputedStyle(link);
      var sidePadding = (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0);
      var items = link.querySelectorAll(ITEM_SELECTOR);

      for (var j = 0; j < items.length; j++) {
        var width = minContentWidth(items[j]) + sidePadding;
        if (width > need) need = width;
      }
    }

    if (need > 0) {
      /* LSC - Round up and add 1px so sub-pixel text never wraps a word anyway. */
      root.style.setProperty('--lsc-card-min', Math.ceil(need) + 1 + 'px');
    } else {
      root.style.removeProperty('--lsc-card-min');
    }
  }

  /* ---------------------------------------------------------------------- */
  /* JOB 2: BALANCED ROWS                                                    */
  /* ---------------------------------------------------------------------- */

  /* LSC - Remove the row breaks this script inserted earlier. */
  function clearBreaks(track) {
    var breaks = track.querySelectorAll('.lsc-arow__break');
    for (var i = 0; i < breaks.length; i++) breaks[i].remove();
  }

  /*
    LSC - Spread a centered phone tiles row evenly over its rows.
      perRow  how many cards fit side by side (from the track's inner width,
              one card's width and the column gap)
      rows    how many rows greedy wrapping would use: ceil(cards / perRow)
      sizes   cards per row, differing by at most one, larger rows first
    A break is inserted after the last card of every row but the final one.
  */
  function balanceRow(root) {
    var track = root.querySelector('.lsc-arow__track');
    if (!track) return;
    clearBreaks(track);

    /* LSC - Only "Mobile layout: Tiles, centered", only at phone width. */
    if (!root.classList.contains('lsc-arow--m-center') || !PHONE.matches) return;

    var cards = track.querySelectorAll('.lsc-arow__card');
    var count = cards.length;
    if (count < 3) return;

    var style = getComputedStyle(track);
    var inner = track.clientWidth - (parseFloat(style.paddingLeft) || 0) - (parseFloat(style.paddingRight) || 0);
    var gap = parseFloat(style.columnGap) || 0;
    var cardWidth = cards[0].getBoundingClientRect().width;
    if (!cardWidth || !inner) return;

    /* LSC - 0.5px slack so rounding never loses a card per row. */
    var perRow = Math.max(1, Math.floor((inner + gap + 0.5) / (cardWidth + gap)));
    if (perRow === 1 || perRow >= count) return;

    var rows = Math.ceil(count / perRow);
    var base = Math.floor(count / rows);
    var extra = count % rows;

    /* LSC - Rows 0..extra-1 hold one more card than the rest. */
    var index = 0;
    for (var r = 0; r < rows - 1; r++) {
      index += base + (r < extra ? 1 : 0);
      var br = document.createElement('li');
      br.className = 'lsc-arow__break';
      br.setAttribute('aria-hidden', 'true');
      br.setAttribute('role', 'presentation');
      cards[index - 1].after(br);
    }
  }

  /*
    LSC - Re-balance a centered row when its track changes width (screen
    rotation, window resize, editor preview width). Only the width matters;
    the height changes the breaks themselves cause are ignored, so the
    observer cannot loop.
  */
  var observed = new WeakSet();
  var resizeObserver = 'ResizeObserver' in window
    ? new ResizeObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          var track = entries[i].target;
          var width = Math.round(entries[i].contentRect.width);
          if (track._lscWidth === width) continue;
          track._lscWidth = width;
          var root = track.closest('.lsc-arow');
          if (root) balanceRow(root);
        }
      })
    : null;

  function watchRow(root) {
    if (!resizeObserver || !root.classList.contains('lsc-arow--m-center')) return;
    var track = root.querySelector('.lsc-arow__track');
    if (!track || observed.has(track)) return;
    observed.add(track);
    resizeObserver.observe(track);
  }

  /* ---------------------------------------------------------------------- */
  /* RUN                                                                     */
  /* ---------------------------------------------------------------------- */

  /* LSC - Width first (it changes card widths), then balance. */
  function measureAll(scope) {
    var rows = (scope || document).querySelectorAll('.lsc-arow');
    for (var i = 0; i < rows.length; i++) {
      measureRow(rows[i]);
      balanceRow(rows[i]);
      watchRow(rows[i]);
    }
  }

  /* LSC - Public hook, also used as the "already set up" flag. */
  window.lscArticleCardWidth = { measure: measureAll, balance: balanceRow };

  /* LSC - First pass: the script is deferred, so the rows are in the page. */
  measureAll(document);

  /* LSC - Second pass once the theme fonts have loaded. */
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(function () {
      measureAll(document);
    });
  }

  /* LSC - Crossing the phone breakpoint adds or removes the breaks. */
  var onBreakpoint = function () {
    var rows = document.querySelectorAll('.lsc-arow--m-center');
    for (var i = 0; i < rows.length; i++) balanceRow(rows[i]);
  };
  if (PHONE.addEventListener) {
    PHONE.addEventListener('change', onBreakpoint);
  } else if (PHONE.addListener) {
    PHONE.addListener(onBreakpoint);
  }

  /* LSC - Theme editor: a section re-rendered after a setting change. */
  document.addEventListener('shopify:section:load', function (event) {
    measureAll(event.target);
  });
})();
